import { useState } from "react";
import { Button, type ToastType } from "@legal-500/legal500-design-system";
import { pipelineService, type Testimonial } from "@/api/pipeline.service";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";

type CascadingStatus = "REJECTED" | "SUSPENDED";

const CASCADE: Record<
  CascadingStatus,
  { title: string; message: string; warning: string; confirm: string; past: string; verb: string }
> = {
  REJECTED: {
    title: "Reject testimonial",
    message: "Rejecting this source testimonial also rejects its translations and copies.",
    warning: "The related testimonials below will be taken down as well.",
    confirm: "Reject all",
    past: "Rejected",
    verb: "reject",
  },
  SUSPENDED: {
    title: "Suspend testimonial",
    message: "Suspending this source testimonial also suspends its translations and copies.",
    warning: "The related testimonials below will be hidden from the site as well.",
    confirm: "Suspend all",
    past: "Suspended",
    verb: "suspend",
  },
};

type TestimonialDecisionActionsProps = {
  testimonial: Testimonial;
  saving: boolean;
  onSavingChange: (saving: boolean) => void;
  onToast: (toast: { message: string; type: ToastType }) => void;
  onLeave: (toastMessage?: string) => void;
};

export function TestimonialDecisionActions({
  testimonial,
  saving,
  onSavingChange,
  onToast,
  onLeave,
}: TestimonialDecisionActionsProps) {
  const [confirming, setConfirming] = useState<CascadingStatus | null>(null);
  const cascades = !testimonial.isTranslation && (testimonial.relatedTestimonials?.length ?? 0) > 0;

  const handleApprove = async () => {
    onSavingChange(true);
    try {
      await pipelineService.updateTestimonial(testimonial.id, { status: "APPROVED" });
      onLeave("Testimonial approved");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to approve";
      onToast({ message: `Request failed: ${msg}`, type: "error" });
    } finally {
      onSavingChange(false);
    }
  };

  const decide = async (status: CascadingStatus) => {
    onSavingChange(true);
    try {
      const response = await pipelineService.updateTestimonial(testimonial.id, { status });
      const family = status === "REJECTED" ? response.takedown : response.suspended;
      const affected = family?.testimonials ?? [];
      const { past } = CASCADE[status];
      const message =
        affected.length > 1
          ? `${past} ${affected.length} testimonials (${affected.map(t => t.language).join(", ")})`
          : `Testimonial ${past.toLowerCase()}`;
      onLeave(message);
    } finally {
      onSavingChange(false);
    }
  };

  const handleDecide = async (status: CascadingStatus) => {
    if (cascades) {
      setConfirming(status);
      return;
    }
    try {
      await decide(status);
    } catch (err) {
      const msg = err instanceof Error ? err.message : `Failed to ${CASCADE[status].verb}`;
      onToast({ message: `Request failed: ${msg}`, type: "error" });
    }
  };

  const canApprove = testimonial.status === "REJECTED" || testimonial.status === "SUSPENDED";
  const canReject = testimonial.status === "APPROVED" || testimonial.status === "SUSPENDED";
  const canSuspend = testimonial.status === "APPROVED";

  return (
    <>
      {canApprove && (
        <Button variant="contained" color="success" onClick={handleApprove} disabled={saving}>
          Approve
        </Button>
      )}
      {canReject && (
        <Button
          variant="contained"
          color="error"
          onClick={() => handleDecide("REJECTED")}
          disabled={saving}
        >
          Reject
        </Button>
      )}
      {canSuspend && (
        <Button
          variant="contained"
          color="warning"
          onClick={() => handleDecide("SUSPENDED")}
          disabled={saving}
        >
          Suspend
        </Button>
      )}

      {confirming && (
        <ConfirmationModal
          title={CASCADE[confirming].title}
          message={CASCADE[confirming].message}
          warningMessage={CASCADE[confirming].warning}
          affectedItems={(testimonial.relatedTestimonials ?? []).map(related => ({
            id: related.id,
            name: related.language.toUpperCase(),
            code: related.id,
          }))}
          confirmButtonText={CASCADE[confirming].confirm}
          confirmButtonColor="error"
          onClose={() => setConfirming(null)}
          onConfirm={() => decide(confirming)}
        />
      )}
    </>
  );
}
